import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing, Dimensions } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { Button, CenteredColumn } from '@/shared/ui';
import { ResultTitle, ResultBody } from '@/shared/resultText';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { useRegistration } from '@/context/RegistrationContext';
import { cmsApiClient, CmsApiError } from '@/services/cmsApiClient';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ERROR_API_CALL, VIEW_ELIGIBILITY_RESULT } from '@/consts/events';
import { env } from '@/env';
import { getCmsEligibleTracks, firstConditionRouteForTracks } from '@/utils/eligibility';
import type { EligibilityTrackResult } from '@/types/registration';

const POLL_INTERVAL_MS = 3_000;
const TAKING_LONGER_MS = 30_000;
const TIMEOUT_MS = 5 * 60_000;

type Phase = 'polling' | 'taking-longer' | 'timeout' | 'error' | 'control-group' | 'already-aligned';

const isTerminal = (r: EligibilityTrackResult) =>
  r.status === 'complete' || r.status === 'error' || r.status === 'timeout';

// Now a POST_REGISTRATION_PATHS route (Layout.tsx): a plain, unframed, normal
// page-scroll screen with no rail/phase-bar to anchor to. min-height +
// justify-content:center keep the splash/result vertically centered in the
// viewport (Boris .rg-proc/.rg-result) instead of clumping at the top of an
// otherwise-empty cream column.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: calc(100vh - ${Dimensions.headerHeight}px);
  gap: ${Spacing.x2}px;
  text-align: center;
  padding: ${Spacing.x4}px ${Spacing.x2}px;
`;

// Boris rg-result-doc variant: left-aligned, slightly smaller, roomier line-height
// for the long verbatim CMS control-group legal text.
const ControlGroupText = styled(ResultBody)`
  text-align: left;
  max-width: 560px;
  font-size: 15.5px;
  line-height: 1.55;
`;

// Boris rg-proc processing splash (register.css .rg-proc-*): a spinning ring +
// sequential status lines. Purely presentational — the real CMS poll drives the
// outcome; these lines reveal on a fixed cadence to convey progress.
const PROC_STATUS_KEYS = [
  'eligibility-result.status-verifying',
  'eligibility-result.status-coverage',
  'eligibility-result.status-assignment',
] as const;

const ProcRing = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 3px solid rgba(28, 31, 33, 0.12);
  border-top-color: ${BorisScreener.ink};
  animation: rg-elig-spin 0.85s linear infinite;
  margin: ${Spacing.x2}px 0;
  @keyframes rg-elig-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProcStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  margin-top: ${Spacing.x1}px;
`;

const ProcLine = styled.div<{ $state: 'upcoming' | 'active' | 'done' }>`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  gap: 9px;
  transition: opacity 0.3s ease;
  color: ${({ $state }) => ($state === 'active' ? BorisScreener.ink : BorisScreener.inkSoft)};
  opacity: ${({ $state }) => ($state === 'done' ? 0.7 : $state === 'active' ? 1 : 0.4)};

  .tick {
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${BorisScreener.btnPrimary};
  }
`;

const CheckIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 13l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EligibilityResultStep: React.FC = () => {
  const { t } = useTranslation();
  useAppFlow(STEP_KEYS.ELIGIBILITY_RESULT);
  const navigate = useNavigate();
  const { registrationData, isLoaded, upsertEligibilityResult, updateEligibility } = useRegistration();
  const { trackError, trackView } = useAnalytics();

  const [phase, setPhase] = useState<Phase>('polling');
  const [retryCount, setRetryCount] = useState(0);
  const [procStep, setProcStep] = useState(0);

  useEffect(() => {
    trackView({ event: VIEW_ELIGIBILITY_RESULT });
  }, [trackView]);

  // Reveal the Boris status lines one at a time while polling (visual only).
  useEffect(() => {
    if (phase !== 'polling' && phase !== 'taking-longer') return;
    if (procStep >= PROC_STATUS_KEYS.length) return;
    const id = setTimeout(() => setProcStep((s) => s + 1), 850);
    return () => clearTimeout(id);
  }, [phase, procStep]);

  const [localResults, setLocalResults] = useState<EligibilityTrackResult[]>(
    () => (registrationData.eligibilityResults ?? []).map(r => ({ ...r })),
  );

  // Ref mirrors localResults and is updated synchronously inside each setLocalResults
  // call so that pollOnce always reads the latest status without waiting for a React
  // re-render. This prevents re-polling tracks that have already settled.
  const localResultsRef = useRef<EligibilityTrackResult[]>(localResults);

  // Tracks submissionIds that have been claimed for polling. Updated synchronously
  // so the interval never re-polls a track whose promise is already in-flight or settled,
  // regardless of whether React has processed the setLocalResults update yet.
  const claimedRef = useRef<Set<string>>(new Set());

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const takingLongerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const stopTimers = useCallback(() => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    if (takingLongerRef.current) { clearTimeout(takingLongerRef.current); takingLongerRef.current = null; }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
  }, []);

  const handleOutcome = useCallback(
    (results: EligibilityTrackResult[]) => {
      stopTimers();

      if (results.some(r => r.resultCode === 'not-eligible-control-group')) {
        setPhase('control-group');
        return;
      }
      if (results.some(r => r.resultCode === 'not-eligible-already-aligned')) {
        setPhase('already-aligned');
        return;
      }

      // Any track poll erroring (network/server failure) is fully recoverable via
      // retry rather than persisted. A mix of complete+error must not silently
      // drop the errored track's eligibility or truncate the questionnaire for
      // the tracks that DID confirm: ELIGIBILITY_RESULT.isCompleted requires
      // ALL results 'complete' (useAppFlow.ts), so persisting a permanent
      // 'error' entry would make it permanently incomplete — HealthConditionsStep's
      // getNextStep() then bounces back to it and finalizes early, skipping the
      // remaining eligible condition screens/screeners (PRD 01 v0.7 §8: "Eligibility
      // API timeout/error → retry prompt ... do not proceed"). Don't persist:
      // context keeps the original processing entries so the retry effect re-reads
      // and re-polls every submissionId (re-polling an already-complete one is a
      // harmless idempotent GET).
      if (results.some(r => r.status === 'error')) {
        setPhase('error');
        return;
      }

      const confirmedTracks = getCmsEligibleTracks(results);

      // Sync terminal results to context. Persisting the resultCodes is required so
      // the downstream condition-step predicates can derive getCmsEligibleTracks.
      results.forEach(r => upsertEligibilityResult(r));

      // RND-36523: eligibility now runs BEFORE the questionnaire. With at least one
      // CMS-eligible track, enter the filtered questionnaire. Do NOT write the final
      // `eligibility` here — it is computed post-questionnaire by useEligibilityFinalize,
      // and keeping it null lets the condition steps drive off the CMS result.
      if (confirmedTracks.length > 0) {
        navigate(firstConditionRouteForTracks(confirmedTracks));
        return;
      }

      // All tracks CMS-ineligible → terminal, skip the questionnaire (PRD §6.2/§8).
      // Explicit route avoids the stale-context race (updateEligibility re-renders).
      updateEligibility({ eligibleTracks: [], isEligible: false, primaryTrack: null });
      navigate('/not-eligible');
    },
    [stopTimers, upsertEligibilityResult, updateEligibility, navigate],
  );

  const pollOnce = useCallback(async () => {
    // Filter to processing tracks not yet claimed. claimedRef is updated synchronously
    // before any async work starts so concurrent/repeated interval calls can't pick up
    // the same submissionId twice, even if React hasn't re-rendered yet.
    const pending = localResultsRef.current.filter(
      r => r.status === 'processing' && r.submissionId && !claimedRef.current.has(r.submissionId!),
    );
    if (pending.length === 0) return;

    pending.forEach(r => claimedRef.current.add(r.submissionId!));

    await Promise.allSettled(
      pending.map(async r => {
        try {
          const response = await cmsApiClient.getEligibilityResult(r.submissionId!);
          if (cancelledRef.current) return;
          if (response.status === 'complete') {
            setLocalResults(prev => {
              const next = prev.map(p =>
                p.track === r.track
                  ? { ...p, status: 'complete' as const, resultCode: response.resultCode }
                  : p,
              );
              localResultsRef.current = next;
              return next;
            });
          } else {
            // Still processing — release claim so next interval tick re-polls.
            // claimedRef dedupes only in-flight requests; holding it across ticks
            // would latch the track as "polled" and silently stop re-polling.
            claimedRef.current.delete(r.submissionId!);
          }
        } catch (err) {
          if (cancelledRef.current) return;
          const status = err instanceof CmsApiError ? err.status : undefined;
          trackError({
            event: ERROR_API_CALL,
            properties: { service: 'cms_eligibility_poll', track: r.track, status: status ?? 'unknown' },
          });
          setLocalResults(prev => {
            const next = prev.map(p =>
              p.track === r.track ? { ...p, status: 'error' as const } : p,
            );
            localResultsRef.current = next;
            return next;
          });
        }
      }),
    );
  }, [trackError]);

  // Start polling loop — re-runs on retry, and once on isLoaded flipping true so a
  // hard-refresh landing directly on this route (context still mid-hydration on
  // mount) re-reads eligibilityResults once the real sessionStorage snapshot loads,
  // instead of latching on the pre-hydration empty default forever.
  useEffect(() => {
    if (!isLoaded) return;

    cancelledRef.current = false;
    claimedRef.current = new Set();
    const initialResults = (registrationData.eligibilityResults ?? []).map(r => ({ ...r }));
    setLocalResults(initialResults);
    localResultsRef.current = initialResults;
    setPhase('polling');

    if (initialResults.length === 0) return;

    pollOnce();
    pollIntervalRef.current = setInterval(() => { pollOnce(); }, POLL_INTERVAL_MS);

    takingLongerRef.current = setTimeout(() => {
      setPhase('taking-longer');
    }, TAKING_LONGER_MS);

    timeoutRef.current = setTimeout(() => {
      cancelledRef.current = true;
      stopTimers();
      setPhase('timeout');
    }, TIMEOUT_MS);

    return () => {
      cancelledRef.current = true;
      stopTimers();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- eligibilityResults snapshot intentional: re-read only on mount/retry/isLoaded-flip, not on every poll tick
  }, [retryCount, pollOnce, stopTimers, isLoaded]);

  // Detect completion via local state.
  useEffect(() => {
    if (localResults.length === 0) return;
    if (localResults.every(isTerminal)) handleOutcome(localResults);
  }, [localResults, handleOutcome]);

  const handleRetry = () => setRetryCount(c => c + 1);

  // ─── Render ────────────────────────────────────────────────────────────

  if (phase === 'control-group') {
    // Verbatim CMS text per CMS ACCESS Participation Agreement — must not be modified.
    return (
      <Container>
        <CenteredColumn span={8}>
          <ResultTitle size="h2" centered>
            {t('eligibility-result.control-group-title')}
          </ResultTitle>
          <ControlGroupText size="p1">
            {t('eligibility-result.control-group-message')}
          </ControlGroupText>
        </CenteredColumn>
      </Container>
    );
  }

  if (phase === 'already-aligned') {
    return (
      <Container>
        <CenteredColumn span={8}>
          <ResultTitle size="h2" centered>
            {t('eligibility-result.title')}
          </ResultTitle>
          <ResultBody size="p1" centered>
            {t('eligibility-result.already-aligned-message')}
          </ResultBody>
        </CenteredColumn>
      </Container>
    );
  }

  if (phase === 'timeout') {
    const email = env.SUPPORT_EMAIL;
    return (
      <Container>
        <CenteredColumn span={8}>
          <ResultTitle size="h2" centered>
            {t('eligibility-result.timeout-title')}
          </ResultTitle>
          <ResultBody size="p1" centered>
            {t('eligibility-result.timeout-message', { email })}
          </ResultBody>
        </CenteredColumn>
      </Container>
    );
  }

  if (phase === 'error') {
    return (
      <Container>
        <CenteredColumn span={8}>
          <ResultTitle size="h2" centered>
            {t('eligibility-result.title')}
          </ResultTitle>
          <ResultBody size="p1" centered>
            {t('eligibility-result.error')}
          </ResultBody>
          <CenteredColumn span={6}>
            <Button
              text={t('eligibility-result.retry')}
              onClick={handleRetry}
              variant="brand-primary"
            />
          </CenteredColumn>
        </CenteredColumn>
      </Container>
    );
  }

  return (
    <Container>
      <CenteredColumn span={8}>
        <ResultTitle size="h2" centered>
          {t('eligibility-result.title')}
        </ResultTitle>
        <ResultBody size="p1" centered>
          {phase === 'taking-longer'
            ? t('eligibility-result.taking-longer')
            : t('eligibility-result.message')}
        </ResultBody>
        <ProcRing aria-hidden="true" />
        <ProcStatus role="status" aria-live="polite">
          {PROC_STATUS_KEYS.map((key, i) => {
            const state = i < procStep ? 'done' : i === procStep ? 'active' : 'upcoming';
            return (
              <ProcLine key={key} $state={state}>
                <span className="tick">{state === 'done' ? <CheckIcon /> : '•'}</span>
                <span>{t(key)}</span>
              </ProcLine>
            );
          })}
        </ProcStatus>
      </CenteredColumn>
    </Container>
  );
};

export { EligibilityResultStep };
